/**
 * Help and knowledge-base citations in a Benny reply.
 *
 * The model writes `[doc:<id>]` for a Help Center article and
 * `[knowledge:<id>]` for a knowledge-base article. Both surfaces rewrite
 * those into markdown links on private schemes, then render the links as
 * chips — one copy of the rewrite here so the docked chat and Conversations
 * can never disagree about what a citation looks like.
 */

export const DOC_CITATION_PATTERN = /\[doc:([A-Za-z0-9._-]+)\]/g
export const KNOWLEDGE_CITATION_PATTERN = /\[knowledge:([A-Za-z0-9-]+)\]/g

export const HELP_CITATION_SCHEME = 'scrambl-help://'
export const KNOWLEDGE_CITATION_SCHEME = 'scrambl-knowledge://'

/** Every distinct Help Center article id cited in the content, in order. */
export function extractDocCitationIds(content?: string | null): string[] {
  if (!content) return []
  const ids = new Set<string>()
  for (const match of content.matchAll(DOC_CITATION_PATTERN)) {
    if (match[1]) ids.add(match[1])
  }
  return [...ids]
}

/** Rewrite bracket citations into links the markdown renderer can chip. */
export function linkDocCitations(content: string): string {
  return content
    .replace(
      DOC_CITATION_PATTERN,
      (_match, id: string) => `[doc:${id}](${HELP_CITATION_SCHEME}${id})`,
    )
    .replace(
      KNOWLEDGE_CITATION_PATTERN,
      (_match, id: string) =>
        `[knowledge:${id}](${KNOWLEDGE_CITATION_SCHEME}${id})`,
    )
}

/** Which citation a rendered link href is, if any. */
export function parseCitationHref(
  href: string,
): { kind: 'help' | 'knowledge'; id: string } | null {
  if (href.startsWith(HELP_CITATION_SCHEME)) {
    return { kind: 'help', id: href.slice(HELP_CITATION_SCHEME.length) }
  }
  if (href.startsWith(KNOWLEDGE_CITATION_SCHEME)) {
    return {
      kind: 'knowledge',
      id: href.slice(KNOWLEDGE_CITATION_SCHEME.length),
    }
  }
  return null
}

/**
 * The chip text for a citation. The link text is the raw `[doc:clients.foo]`
 * token; readers want the article, not the id, so the last id segment is
 * humanized ("clients.client-portal" → "Client portal").
 */
export function citationLabel(id: string): string {
  const last = id.split('.').pop() ?? id
  const words = last.replace(/[-_]+/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : id
}
